import { Drawer } from '@base-ui/react/drawer';
import { cn } from '@foundation/utils/cn';
import {
  ComponentPropsWithoutRef,
  createContext,
  CSSProperties,
  Fragment,
  ReactElement,
  ReactNode,
  useContext,
} from 'react';

import { ensurePortalRoot } from '../primitives/ensurePortalRoot';
import { useMediaQuery } from '../primitives/useMediaQuery';

export type HeadlessDrawerStyles = {
  readonly container?: string;
  readonly overlay?: string;
  readonly panel?: string;
  readonly swipeArea?: string;
  readonly swipeHandle?: string;
  readonly closeButton?: string;
  readonly closeButtonContainer?: string;
  readonly header?: string;
  readonly body?: string;
  readonly buttonGroup?: string;
  readonly title?: string;
  readonly description?: string;
};

export type HeadlessDrawerPosition = 'left' | 'right';

/**
 * `side` is the desktop side panel, `sheet` the mobile bottom sheet. Only pass
 * this when the consumer resolves the breakpoint itself - by default the drawer
 * picks the presentation from its own media query.
 */
export type HeadlessDrawerPresentation = 'side' | 'sheet';

/**
 * Per-reason dismissal policy. Each entry enables one way for the user to
 * dismiss the drawer; `close-press` (an explicit close control) is always
 * allowed. Use this when a drawer needs a visible close button but must not
 * dismiss itself - `closePrevented` removes the affordances too.
 */
export type HeadlessDrawerDismiss = {
  readonly outsidePress?: boolean;
  readonly escapeKey?: boolean;
  readonly focusOut?: boolean;
  readonly swipe?: boolean;
};

const DEFAULT_DISMISS = {
  outsidePress: true,
  escapeKey: true,
  focusOut: true,
  swipe: true,
} as const satisfies Required<HeadlessDrawerDismiss>;

const NO_DISMISS = {
  outsidePress: false,
  escapeKey: false,
  focusOut: false,
  swipe: false,
} as const satisfies Required<HeadlessDrawerDismiss>;

export type HeadlessDrawerRootProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly position?: HeadlessDrawerPosition;
  /**
   * Blocks every dismissal reason except `close-press`, and hides the close
   * button and swipe handle. Shorthand for a drawer that may only be dismissed
   * through a consumer-rendered `HeadlessDrawer.Close`. Takes precedence over
   * `dismiss`.
   */
  readonly closePrevented?: boolean;
  readonly dismiss?: HeadlessDrawerDismiss;
  /**
   * Whether the drawer traps focus, locks page scroll, and makes outside
   * content inert. Pass `false` for a drawer that sits alongside the page
   * rather than over it.
   * @default true
   */
  readonly modal?: boolean | 'trap-focus';
  /**
   * Overrides the internal breakpoint query. Needed when the presentation
   * depends on more than the viewport width.
   */
  readonly presentation?: HeadlessDrawerPresentation;
  /**
   * Whether to render the backdrop element. A backdrop still participates in
   * the stacking context even when fully transparent, so panels that sit
   * alongside the page rather than over it should turn it off.
   * @default true
   */
  readonly showBackdrop?: boolean;
  /**
   * Enables Base UI's keyboard-aware focus and scroll handling and exposes
   * `--drawer-keyboard-inset`. Only meaningful for sheets containing form fields.
   * @default false
   */
  readonly virtualKeyboard?: boolean;
  readonly showCloseButton?: boolean;
  readonly initialFocus?: ComponentPropsWithoutRef<
    typeof Drawer.Popup
  >['initialFocus'];
  readonly finalFocus?: ComponentPropsWithoutRef<
    typeof Drawer.Popup
  >['finalFocus'];
  readonly closeLabel?: string;
  readonly closeButton?: ReactElement;
  readonly styles?: HeadlessDrawerStyles;
  /**
   * Inline style for the viewport element. For runtime numeric values that
   * can't be expressed as a Tailwind class, such as a pixel offset.
   */
  readonly containerStyle?: CSSProperties;
  readonly portalId?: string;
  /** Forwarded to the popup so consumers can target it with `aria-controls`. */
  readonly id?: string;
  /** Accessible name for the popup, for drawers without a visible title. */
  readonly 'aria-label'?: string;
  readonly 'data-testid'?: string;
};

type HeadlessDrawerContextValue = {
  readonly styles: HeadlessDrawerStyles;
};

const HeadlessDrawerContext = createContext<HeadlessDrawerContextValue | null>(
  null,
);

function useHeadlessDrawerContext(
  component: string,
): HeadlessDrawerContextValue {
  const context = useContext(HeadlessDrawerContext);
  if (!context) {
    throw new Error(`${component} must be used within HeadlessDrawer.Root`);
  }
  return context;
}

// Below `md` (768px, matches --breakpoint-md) the drawer always renders as a
// swipeable bottom sheet, regardless of the requested `position`.
const DESKTOP_QUERY = '(min-width: 768px)';

function Root({
  isOpen,
  onClose,
  children,
  position = 'right',
  closePrevented = false,
  dismiss,
  modal = true,
  presentation,
  showBackdrop = true,
  virtualKeyboard = false,
  showCloseButton = true,
  initialFocus,
  finalFocus,
  closeLabel = 'Close',
  closeButton,
  styles = {},
  containerStyle,
  portalId = 'pmi-drawer-root',
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: HeadlessDrawerRootProps) {
  const matchesDesktop = useMediaQuery(DESKTOP_QUERY);
  const isDesktop = presentation ? presentation === 'side' : matchesDesktop;
  const container = ensurePortalRoot(portalId);

  // Has to sit inside Drawer.Root - it reads the dialog store and feeds the viewport's
  // `--drawer-keyboard-inset`.
  const KeyboardProvider = virtualKeyboard
    ? Drawer.VirtualKeyboardProvider
    : Fragment;

  const dismissPolicy = closePrevented
    ? NO_DISMISS
    : { ...DEFAULT_DISMISS, ...dismiss };

  const handleOpenChange = (
    nextOpen: boolean,
    eventDetails: Drawer.Root.ChangeEventDetails,
  ) => {
    if (nextOpen) return;

    const { reason } = eventDetails;

    // Side drawers (desktop) never dismiss via swipe - only the mobile bottom
    // sheet supports swipe-to-close. Cancel so the panel springs back rather
    // than resting wherever the drag ended.
    if (reason === 'swipe' && (isDesktop || !dismissPolicy.swipe)) {
      eventDetails.cancel();
      return;
    }

    // `closePrevented` is absolute: nothing but an explicit close control
    // dismisses the drawer, including reasons the policy below doesn't name.
    if (closePrevented && reason !== 'close-press') return;

    if (reason === 'outside-press' && !dismissPolicy.outsidePress) return;
    if (reason === 'focus-out' && !dismissPolicy.focusOut) return;
    // `close-watcher` is the same intent as Escape, routed through the
    // CloseWatcher API (Android back gesture, for instance).
    if (
      (reason === 'escape-key' || reason === 'close-watcher') &&
      !dismissPolicy.escapeKey
    ) {
      return;
    }

    onClose();
  };

  const closeButtonSlot = showCloseButton && !closePrevented && (
    <div
      className={cn('absolute right-0 top-0 z-20', styles.closeButtonContainer)}
    >
      <Drawer.Close
        render={closeButton}
        className={cn('cursor-pointer', styles.closeButton)}
        aria-label={closeLabel}
        data-testid={dataTestId ? `${dataTestId}-close-button` : undefined}
      />
    </div>
  );

  if (isDesktop) {
    return (
      <HeadlessDrawerContext.Provider value={{ styles }}>
        <Drawer.Root
          open={isOpen}
          onOpenChange={handleOpenChange}
          swipeDirection={position}
          modal={modal}
          disablePointerDismissal={!dismissPolicy.outsidePress}
        >
          <KeyboardProvider>
            <Drawer.Portal container={container}>
              {showBackdrop && (
                <Drawer.Backdrop
                  className={cn(
                    'fixed inset-0 z-40 transition-opacity duration-300 ease-out',
                    'data-starting-style:opacity-0 data-ending-style:opacity-0',
                    styles.overlay,
                  )}
                  data-testid={
                    dataTestId ? `${dataTestId}-backdrop` : undefined
                  }
                />
              )}

              <Drawer.Viewport
                style={containerStyle}
                className={cn(
                  'fixed inset-0 z-50 flex items-center',
                  position === 'right' ? 'justify-end' : 'justify-start',
                  styles.container,
                )}
              >
                <Drawer.Popup
                  id={id}
                  aria-label={ariaLabel}
                  initialFocus={initialFocus}
                  finalFocus={finalFocus}
                  data-testid={dataTestId}
                  className={cn(
                    'pointer-events-auto relative flex h-full flex-col overflow-hidden',
                    '[transform:translateX(var(--drawer-swipe-movement-x,0px))]',
                    'transition-transform duration-300 ease-out',
                    'data-[swipe-direction=right]:data-starting-style:[transform:translateX(100%)]',
                    'data-[swipe-direction=right]:data-ending-style:[transform:translateX(100%)]',
                    'data-[swipe-direction=left]:data-starting-style:[transform:translateX(-100%)]',
                    'data-[swipe-direction=left]:data-ending-style:[transform:translateX(-100%)]',
                    styles.panel,
                  )}
                >
                  {/* The whole panel is wrapped in Drawer.Content: Base UI ignores pointer-based
                    swipes starting inside it, and swipe-reason closes are canceled above too -
                    side drawers never dismiss via swipe. */}
                  <Drawer.Content className="relative flex h-full flex-col">
                    {closeButtonSlot}
                    {children}
                  </Drawer.Content>
                </Drawer.Popup>
              </Drawer.Viewport>
            </Drawer.Portal>
          </KeyboardProvider>
        </Drawer.Root>
      </HeadlessDrawerContext.Provider>
    );
  }

  return (
    <HeadlessDrawerContext.Provider value={{ styles }}>
      <Drawer.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        swipeDirection="down"
        modal={modal}
        disablePointerDismissal={!dismissPolicy.outsidePress}
      >
        <KeyboardProvider>
          <Drawer.Portal container={container}>
            {showBackdrop && (
              <Drawer.Backdrop
                className={cn(
                  'fixed inset-0 z-40 transition-opacity duration-300 ease-out',
                  'data-starting-style:opacity-0 data-ending-style:opacity-0',
                  styles.overlay,
                )}
                data-testid={dataTestId ? `${dataTestId}-backdrop` : undefined}
              />
            )}

            <Drawer.Viewport
              style={containerStyle}
              className={cn(
                'fixed inset-0 z-50 flex items-end justify-center',
                styles.container,
              )}
            >
              <Drawer.Popup
                id={id}
                aria-label={ariaLabel}
                initialFocus={initialFocus}
                finalFocus={finalFocus}
                data-testid={dataTestId}
                className={cn(
                  'pointer-events-auto relative flex w-full flex-col overflow-hidden',
                  '[transform:translateY(var(--drawer-swipe-movement-y,0px))]',
                  'transition-transform duration-300 ease-out',
                  'data-starting-style:[transform:translateY(100%)] data-ending-style:[transform:translateY(100%)]',
                  styles.panel,
                )}
              >
                {dismissPolicy.swipe && (
                  <div
                    className={cn(
                      'relative z-10 flex h-10 w-full shrink-0 items-center justify-center',
                      styles.swipeArea,
                    )}
                    data-testid={
                      dataTestId ? `${dataTestId}-swipe-area` : undefined
                    }
                  >
                    <div
                      className={cn('pointer-events-none', styles.swipeHandle)}
                    />
                  </div>
                )}

                {closeButtonSlot}

                {/* Drawer.Content opts mouse/pen out of swipe-dismiss (touch is unaffected) - the
                  swipe handle above lives outside Content so both input types can drag it. */}
                <Drawer.Content className="flex min-h-0 flex-1 flex-col">
                  {children}
                </Drawer.Content>
              </Drawer.Popup>
            </Drawer.Viewport>
          </Drawer.Portal>
        </KeyboardProvider>
      </Drawer.Root>
    </HeadlessDrawerContext.Provider>
  );
}

function Header({ className, ...props }: ComponentPropsWithoutRef<'header'>) {
  const { styles } = useHeadlessDrawerContext('HeadlessDrawer.Header');
  return (
    <header
      className={cn('flex items-center gap-2', styles.header, className)}
      {...props}
    />
  );
}

function Body({ className, ...props }: ComponentPropsWithoutRef<'section'>) {
  const { styles } = useHeadlessDrawerContext('HeadlessDrawer.Body');
  return (
    <section
      className={cn('flex-1 overflow-y-auto', styles.body, className)}
      {...props}
    />
  );
}

function ButtonGroup({
  className,
  ...props
}: ComponentPropsWithoutRef<'footer'>) {
  const { styles } = useHeadlessDrawerContext('HeadlessDrawer.ButtonGroup');
  return (
    <footer
      className={cn(styles.buttonGroup, className)}
      {...props}
    />
  );
}

function Title({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Drawer.Title>) {
  const { styles } = useHeadlessDrawerContext('HeadlessDrawer.Title');
  return (
    <Drawer.Title
      className={cn(styles.title, className)}
      {...props}
    />
  );
}

function Description({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Drawer.Description>) {
  const { styles } = useHeadlessDrawerContext('HeadlessDrawer.Description');
  return (
    <Drawer.Description
      className={cn(styles.description, className)}
      {...props}
    />
  );
}

type CloseProps = {
  readonly render: ReactElement;
};

function Close({ render }: CloseProps) {
  useHeadlessDrawerContext('HeadlessDrawer.Close');

  return <Drawer.Close render={render} />;
}

export const HeadlessDrawer = {
  Root,
  Header,
  Body,
  ButtonGroup,
  Title,
  Description,
  Close,
};
